// ./src/hooks/useUploadedFiles.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  UploadedFile,
  UploadedFileInput,
  UploadedFileFilter,
  UploadedFileWhereInput,
  UploadedFileService,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseUploadedFilesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<UploadedFileFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseUploadedFilesReturn {
  // Data state
  uploadedFile: UploadedFile | null;
  uploadedFiles: UploadedFile[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<UploadedFileFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingFileId: string | null;
  selectedFile: UploadedFile | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<UploadedFileFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<UploadedFile[]>;
  create: (params: UploadedFileInput) => Promise<UploadedFile | null>;
  update: (
    where: UploadedFileWhereInput,
    params: UploadedFileInput
  ) => Promise<UploadedFile | null>;
  remove: (where: UploadedFileWhereInput) => Promise<boolean>;
  removeMany: (where: UploadedFileWhereInput[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<UploadedFileFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedFile: (file: UploadedFile | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  totalSize: number;
  filesByMimeType: Record<string, UploadedFile[]>;
  imageFiles: UploadedFile[];
  documentFiles: UploadedFile[];
  videoFiles: UploadedFile[];

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useUploadedFiles = (
  options: UseUploadedFilesOptions = {}
): UseUploadedFilesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<UploadedFileFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingFileId, setRemovingFileId] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => uploadedFiles.length === 0 && !loading,
    [uploadedFiles.length, loading]
  );

  const totalSize = useMemo(() => {
    return uploadedFiles.reduce((sum, file) => sum + (file.sizeBytes || 0), 0);
  }, [uploadedFiles]);

  const filesByMimeType = useMemo(() => {
    return uploadedFiles.reduce((acc, file) => {
      const mimeType = file.mimeType || "unknown";
      if (!acc[mimeType]) {
        acc[mimeType] = [];
      }
      acc[mimeType].push(file);
      return acc;
    }, {} as Record<string, UploadedFile[]>);
  }, [uploadedFiles]);

  const imageFiles = useMemo(() => {
    return uploadedFiles.filter((file) => file.mimeType?.startsWith("image/"));
  }, [uploadedFiles]);

  const documentFiles = useMemo(() => {
    return uploadedFiles.filter(
      (file) =>
        file.mimeType?.includes("pdf") ||
        file.mimeType?.includes("document") ||
        file.mimeType?.includes("text") ||
        file.mimeType?.includes("spreadsheet") ||
        file.mimeType?.includes("presentation")
    );
  }, [uploadedFiles]);

  const videoFiles = useMemo(() => {
    return uploadedFiles.filter((file) => file.mimeType?.startsWith("video/"));
  }, [uploadedFiles]);

  // Helper function to update file in list if it exists, or add it if not
  const updateOrAddFileInList = useCallback((updatedFile: UploadedFile) => {
    setUploadedFiles((prev) => {
      const existingIndex = prev.findIndex((f) => f.id === updatedFile.id);
      if (existingIndex >= 0) {
        const newFiles = [...prev];
        newFiles[existingIndex] = updatedFile;
        return newFiles;
      } else {
        return [updatedFile, ...prev];
      }
    });
  }, []);

  // Helper function to remove file from list if it exists
  const removeFileFromList = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  // Helper function to remove multiple files from list
  const removeFilesFromList = useCallback((fileIds: string[]) => {
    const idsSet = new Set(fileIds);
    setUploadedFiles((prev) => prev.filter((f) => !idsSet.has(f.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setUploadedFile(null);
    setUploadedFiles([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingFileId(null);
    setSelectedFile(null);
  }, []);

  // Get single uploaded file (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await UploadedFileService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const fileData = result.data[0] as UploadedFile;
          setUploadedFile(fileData);
          updateOrAddFileInList(fileData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load uploaded file";
          setLastError(errorMessage);
          setUploadedFile(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load uploaded file";
        setLastError(errorMessage);
        setUploadedFile(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddFileInList]
  );

  // List uploaded files (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<UploadedFileFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setUploadedFiles([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await UploadedFileService.list(
          filter as UploadedFileFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newFiles = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setUploadedFiles((prev) => {
              const existingIds = new Set(prev.map((f) => f.id));
              const uniqueNewFiles = newFiles.filter(
                (f) => !existingIds.has(f.id)
              );
              return [...prev, ...uniqueNewFiles];
            });
          } else {
            setUploadedFiles(newFiles);
          }

          setHasMore(newFiles.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load uploaded files";
          setLastError(errorMessage);
          if (!isAppending) setUploadedFiles([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load uploaded files";
        setLastError(errorMessage);
        if (!isAppending) setUploadedFiles([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many uploaded files by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<UploadedFile[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await UploadedFileService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedFiles = Array.isArray(result.data) ? result.data : [];
          loadedFiles.forEach((file) => {
            updateOrAddFileInList(file);
          });
          setLastError(null);
          return loadedFiles;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load uploaded files";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load uploaded files";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddFileInList]
  );

  // Create uploaded file (matching API method name)
  const create = useCallback(
    async (params: UploadedFileInput): Promise<UploadedFile | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await UploadedFileService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newFile = result.data[0] as UploadedFile;
          setUploadedFile(newFile);
          setUploadedFiles((prev) => [newFile, ...prev]);
          setLastError(null);
          return newFile;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create uploaded file";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create uploaded file";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update uploaded file (matching API method name)
  const update = useCallback(
    async (
      where: UploadedFileWhereInput,
      params: UploadedFileInput
    ): Promise<UploadedFile | null> => {
      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UploadedFileService.update(where, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedFile = result.data[0] as UploadedFile;
          setUploadedFile(updatedFile);
          updateOrAddFileInList(updatedFile);
          setLastError(null);
          return updatedFile;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update uploaded file";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update uploaded file";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddFileInList]
  );

  // Remove uploaded file (matching API method name)
  const remove = useCallback(
    async (where: UploadedFileWhereInput): Promise<boolean> => {
      const fileId = where.id;
      if (fileId) {
        setRemovingFileId(fileId);
      }
      setLastError(null);

      try {
        const result = await UploadedFileService.remove(where);

        if (result.code === OP_RESULT_CODES.OK) {
          setUploadedFile(null);
          if (fileId) {
            removeFileFromList(fileId);
          }
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove uploaded file";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove uploaded file";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingFileId(null);
      }
    },
    [removeFileFromList]
  );

  // Remove many uploaded files (matching API method name)
  const removeMany = useCallback(
    async (where: UploadedFileWhereInput[]): Promise<boolean> => {
      if (where.length === 0) {
        setLastError("At least one file is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await UploadedFileService.removeMany(where);

        if (result.code === OP_RESULT_CODES.OK) {
          const fileIds = where.map((w) => w.id).filter(Boolean) as string[];
          setUploadedFile(null);
          removeFilesFromList(fileIds);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove uploaded files";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove uploaded files";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeFilesFromList]
  );

  // Load more uploaded files
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh uploaded files
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    uploadedFile,
    uploadedFiles,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingFileId,
    selectedFile,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedFile,
    reset,
    totalSize,
    filesByMimeType,
    imageFiles,
    documentFiles,
    videoFiles,
    lastError,
    clearError,
  };
};
